import { useEffect } from 'react';
import { Capacitor } from '@capacitor/core';
import { App } from '@capacitor/app';
import { sync, reload } from '@capacitor/live-updates';
import type { createBrowserRouter } from 'react-router-dom';
import * as Sentry from '@sentry/capacitor';

import { LIVE_UPDATES_ENABLED } from '../env';

type AppRouter = ReturnType<typeof createBrowserRouter>;

/**
 * Keeps the native app on the latest Ionic Appflow live-update bundle without
 * ever blocking the UI:
 *
 *   1. On launch (and every time the app returns to the foreground) we check
 *      Appflow for a newer bundle in the background. The user keeps using the
 *      installed version the whole time.
 *   2. When a newer bundle has been staged, we don't reload immediately — that
 *      would tear the app down mid-screen. Instead we wait for the next route
 *      change (a natural boundary where losing transient state is expected) and
 *      reload into the new version then.
 *
 * If we never get a chance to reload this session, the staged bundle is already
 * the active path, so the next cold launch boots straight into it — no update
 * is ever lost.
 *
 * On web (or any non-native platform) live updates don't apply, so this is a
 * no-op. It is also a no-op when LIVE_UPDATES_ENABLED is off — e2e builds turn it
 * off so a run always exercises the bundle built from that commit rather than
 * whatever is currently published to the Appflow channel.
 */
export function useLiveUpdates(router: AppRouter): void {
  useEffect(() => {
    if (!Capacitor.isNativePlatform() || !LIVE_UPDATES_ENABLED) return;

    let cancelled = false;
    let updateReady = false;
    let reloading = false;
    let lastPath = router.state.location.pathname;

    const checkForUpdate = async () => {
      try {
        const result = await sync();
        if (cancelled) return;
        if (result.activeApplicationPathChanged) updateReady = true;
      } catch (err) {
        // Offline or an Appflow error — never disrupt the user; keep running the
        // installed version and try again on the next foreground.
        Sentry.captureException(err);
      }
    };

    const reloadIfReady = () => {
      if (!updateReady || reloading) return;
      reloading = true;
      // Reboots the webview into the staged bundle. Nothing after this matters.
      reload();
    };

    // Check on launch and whenever the app is resumed, so a long-lived session
    // still picks up bundles shipped after the initial launch.
    checkForUpdate();
    const resumeHandle = App.addListener('resume', checkForUpdate);

    // Apply a staged update the moment the user navigates to a different route.
    const unsubscribe = router.subscribe((state) => {
      const path = state.location.pathname;
      if (path === lastPath) return;
      lastPath = path;
      reloadIfReady();
    });

    return () => {
      cancelled = true;
      unsubscribe();
      resumeHandle.then((handle) => handle.remove());
    };
  }, [router]);
}
